import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { timeAgo } from "@/lib/format";

export const Reveal = ({ children, delay = 0, className }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Cover = ({ a, className = "", eager }) => (
  <div className={`relative overflow-hidden bg-muted ${className}`}>
    {a.image_url ? (
      <img
        src={imgUrl(a.image_url)}
        alt={a.image_alt || a.title}
        loading={eager ? "eager" : "lazy"}
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
    ) : (
      <div className="h-full w-full cover-fallback grid place-items-center">
        <span className="font-display font-black text-3xl text-primary/25">EN</span>
      </div>
    )}
  </div>
);

export const Kicker = ({ a, className = "text-primary" }) => <span className={`kicker ${className}`}>{a.category_name}</span>;

export const Meta = ({ a, className = "text-muted-foreground", short }) => (
  <div className={`font-mono text-[11px] uppercase tracking-[0.12em] flex flex-wrap items-center gap-x-2.5 ${className}`}>
    <time dateTime={a.published_at} className="whitespace-nowrap">{timeAgo(a.published_at)}</time>
    <span aria-hidden>·</span>
    <span className="whitespace-nowrap">{a.reading_time || 1} min{short ? "" : " de lectura"}</span>
  </div>
);

const to = (a) => `/noticia/${a.slug}`;

export const LeadStory = ({ a }) => (
  <article className="group" data-testid="lead-story">
    <Link to={to(a)} className="block" data-testid="lead-story-link">
      <Cover a={a} eager className="aspect-[16/9]" />
      <div className="pt-6">
        <Kicker a={a} />
        <h2 className="font-display font-extrabold tracking-[-0.02em] leading-[1.02] text-4xl sm:text-5xl lg:text-[3.5rem] mt-3 transition-colors group-hover:text-primary">
          {a.title}
        </h2>
        <p className="mt-5 text-lg text-muted-foreground leading-relaxed max-w-2xl">{a.summary}</p>
        <div className="mt-5"><Meta a={a} /></div>
      </div>
    </Link>
  </article>
);

export const StackStory = ({ a, index }) => (
  <article className="group py-5 border-b border-border first:pt-0 last:border-0" data-testid={`top-story-${index}`}>
    <Link to={to(a)} className="flex gap-5">
      <div className="flex-1 min-w-0">
        <Kicker a={a} />
        <h3 className="font-display font-bold text-xl leading-snug mt-1.5 transition-colors group-hover:text-primary">{a.title}</h3>
        <div className="mt-2.5"><Meta a={a} short /></div>
      </div>
      <Cover a={a} className="w-24 h-24 sm:w-32 sm:h-24 shrink-0" />
    </Link>
  </article>
);

export const GridStory = ({ a, testid, large }) => (
  <article className="group" data-testid={testid}>
    <Link to={to(a)} className="block">
      <Cover a={a} className={large ? "aspect-[16/10]" : "aspect-[3/2]"} />
      <div className="pt-4">
        <Kicker a={a} />
        <h3 className={`font-display font-bold leading-snug mt-1.5 transition-colors group-hover:text-primary ${large ? "text-3xl" : "text-xl"}`}>{a.title}</h3>
        <p className={`mt-2 text-muted-foreground leading-relaxed ${large ? "text-base line-clamp-3" : "text-[15px] line-clamp-2"}`}>{a.summary}</p>
        <div className="mt-3"><Meta a={a} /></div>
      </div>
    </Link>
  </article>
);

export const SectionHeader = ({ title, to: href, kicker, dark }) => (
  <div className={`flex items-end justify-between gap-4 border-t-2 pt-3 mb-8 ${dark ? "border-white" : "border-foreground"}`}>
    <div>
      {kicker && <p className={`kicker mb-1 ${dark ? "text-gold" : "text-primary"}`}>{kicker}</p>}
      <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">{title}</h2>
    </div>
    {href && (
      <Link to={href} className="kicker flex items-center gap-1 hover:text-primary transition-colors shrink-0" data-testid={`section-more-${href.split("/").pop()}`}>
        Ver todo <ArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    )}
  </div>
);
